import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { patchAccount, type ImageDisplayMode, type MailAccount } from '../lib/api'

const MODES: ImageDisplayMode[] = ['ask', 'always', 'never']

type Props = {
  account: MailAccount
  /** Compact = banner dropdown; full = settings section */
  variant?: 'full' | 'compact'
  onUpdated?: (mode: ImageDisplayMode) => void
}

/**
 * FAZA 1.5 — account-level remote image roof (ask|always|never).
 * Does not replace per-sender / load-once controls.
 */
export function ImageDisplaySettings({ account, variant = 'full', onUpdated }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(variant === 'full')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const current: ImageDisplayMode = account.image_display_mode || 'ask'

  async function apply(mode: ImageDisplayMode) {
    if (mode === current || busy) return
    if (mode === 'always') {
      const ok = window.confirm(t('reader.imageAlwaysWarning'))
      if (!ok) return
    }
    setBusy(true)
    setErr(null)
    try {
      await patchAccount(account.id, { image_display_mode: mode })
      onUpdated?.(mode)
      if (variant === 'compact') setOpen(false)
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('reader.loadFailed'))
    } finally {
      setBusy(false)
    }
  }

  const radios = (
    <div
      className="rd-img-mode-list"
      role="radiogroup"
      aria-label={t('mail.imageDisplayTitle')}
    >
      {MODES.map((mode) => (
        <label key={mode} className={`rd-img-mode-opt${current === mode ? ' sel' : ''}`}>
          <input
            type="radio"
            name={`img-mode-${account.id}-${variant}`}
            value={mode}
            checked={current === mode}
            disabled={busy}
            onChange={() => void apply(mode)}
          />
          <span>
            <strong>{t(`mail.imageDisplay.${mode}`)}</strong>
            <span className="d" style={{ display: 'block', marginTop: 2 }}>
              {t(`mail.imageDisplay.${mode}Hint`)}
            </span>
          </span>
        </label>
      ))}
      {err ? (
        <p className="d" role="alert" style={{ color: 'var(--rd-danger, #b91c1c)' }}>
          {err}
        </p>
      ) : null}
    </div>
  )

  if (variant === 'compact') {
    return (
      <div className="rd-img-mode-compact">
        <button
          type="button"
          className="mail-tbtn rd-img-mode-link"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {t('reader.changeAccountImageDefault')}
        </button>
        {open ? radios : null}
      </div>
    )
  }

  return (
    <div className="rd-acct-images" style={{ marginTop: 18 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 14 }}>{t('mail.imageDisplayTitle')}</h3>
      <p className="d">{t('mail.imageDisplayLead')}</p>
      {radios}
    </div>
  )
}
